/**
 * clasificadores/velocidadConsumo.js — Lógica de clasificación del dashboard
 * de Velocidad de Consumo de Mercado Libre.
 *
 * Función pura: recibe datos crudos de Firestore, devuelve secciones
 * clasificadas con items accionables.
 *
 * Consumidores:
 *   1. Agente de Velocidad de Consumo (vía `ejecutarAgente`).
 *   2. El dashboard: `calcularPedido`, `filasVelocidadConsumo` (las filas de
 *      velocidad) y `seccionesVelocidadConsumo` (las secciones accionables),
 *      `categoryIdDePublicacion`.
 *
 * Input — viene de `cargarDatosParaClasificador` en agentes.js:
 *   - publicaciones        : array de mlPublicaciones
 *   - ventas30d            : array de mlOrdenes de los últimos N días
 *   - adsItems             : array de mlAds (status, item_id)
 *   - productos            : array de productos del catálogo de la empresa
 *   - catalogoLinks        : array de links producto ↔ publicación
 *   - mlPublicacionesVisitas: array de docs con visitas/30d por pub
 *   - ventanaVentasDias    : días con los que el loader cargó `ventas30d`
 *                            (agentes.js lo publica en `datos.ventanaVentasDias`)
 *   - umbrales (opcional)  : overrides del agente (diasCubrir, umbral de visitas)
 *
 * Output:
 *   {
 *     secciones: {
 *       reponer_quiebre       : array,  // stock = 0 ya quebrado
 *       reponer_velocidad     : array,  // urgencia crítica o baja
 *       activar_promo_sobrado : array,  // sobrestockeadas que requieren promo
 *       activar_ad_sobrado    : array,  // sobrestockeadas que requieren ad
 *     },
 *     analisis: { total, porUrgencia, sinVentana, sumStock, sumValuacion, sumPotencial, ventanaDias, … },
 *     gateBloqueados: { activar_promo_sobrado: [itemIds] },  // cancelación retroactiva (motor)
 *     lecturas?: { fuentes, degradaciones },  // SÓLO sin ventana: el motor la declara degradada
 *   }
 *
 * Una sola copia de las reglas (report gqpLaPCkeyOj9uvmF5xg): el dashboard y el
 * agente arman filas y secciones con `filasVelocidadConsumo` y
 * `seccionesVelocidadConsumo`. El agente las proyecta con `filaAgente` a la forma
 * que entrega al motor y conserva el orden de entrada; el cooldown de bitácora
 * sale de `cooldownDesdeBitacora`, como en la vista. Lo único que arma a mano es
 * la rama sin ventana, que es otro contrato (todo en null).
 */

import { URGENCIA, calcularUmbrales, clasificarUrgencia, MULT_OK } from '@core/lib/urgenciaStock'
import { esAdVivo } from '@modules/mercadolibre/lib/promoCanonicoShape'
import { construirInfoRentabilidadPorItem, gatePromoConCostosReales, metricasAdsPorItem, gateAdsPrender, textoGateAds } from './gatePromoRentabilidad'
import { estructuraCostosML } from '@core/lib/rentabilidadEstimada'
import { ventanaDeVentas, FUENTE_VENTANA } from './ventanaDeVentas'
import { cooldownDesdeBitacora, PLATAFORMA_ADS_ML } from '../bitacora'
import { CANAL_ML } from '@core/lib/filtrosCanal'

// Única copia de la fórmula de pedido: la importa también la vista
// (VelocidadConsumoDashboard.jsx). Es piecewise para evitar el sobreestimado
// clásico cuando stock < V × N (ítem que va a quebrar antes de recibir el pedido).
//   consumo_durante_espera = min(stock, V × N)
//   pedido = V × objetivoDiasAlRecibir − stock + consumo_durante_espera
export function calcularPedido({ velocidadDiaria, stock, leadTimeDias, objetivoDiasAlRecibir }) {
  if (!Number.isFinite(velocidadDiaria) || velocidadDiaria <= 0) return 0
  const N = Math.max(0, Number(leadTimeDias) || 0)
  const objetivo = Math.max(0, Number(objetivoDiasAlRecibir) || 0)
  const consumoEspera = Math.min(stock, velocidadDiaria * N)
  const necesario = velocidadDiaria * objetivo - stock + consumoEspera
  return Math.max(0, Math.ceil(necesario))
}

// ─── Filas de velocidad por publicación ───────────────────────────────────────
// Extraída tal cual del memo `rowsConVelocidad` de VelocidadConsumoDashboard.jsx
// (report gqpLaPCkeyOj9uvmF5xg, PR 1): velocidad diaria, días a quiebre, urgencia,
// cobertura, plata perdida / en riesgo y pedido sugerido, para las publicaciones
// CON ventas en la ventana, ordenadas por urgencia. La vista y el agente usan el
// MISMO orden: el motor recorta cada sección a los primeros N del lote, así que
// este orden decide qué entra a la tarea (report scoEqWsgJX991pQovyvL). Es pura: no lee el reloj (`ahoraMs` entra
// por parámetro) y opera en instantes en ms, sin agrupar por día. Con `ahoraMs`
// null no se fecha nada: `_fechaQuiebre` y `_diasSinVender` salen null y
// `_plataPerdida` 0, en vez de contar desde 1970.
//   items                   : publicaciones (shape cascada: itemId|id, available_quantity|availableQuantity, price)
//   ventas                  : órdenes (shape cascada: date_created, order_items[{item:{id}, quantity, unit_price}])
//   tiempoReposicionPorItem : Map itemId → { dias, productoId, costoUnit }
//   modo                    : 'generico' | 'reposicion' | 'mixto'
export function filasVelocidadConsumo({
  items = [],
  ventas = [],
  ventanaDias,
  tiempoReposicionPorItem = new Map(),
  modo,
  diasCubrir,
  ahoraMs = null,
}) {
  if (!items?.length) return []

  // Agregar unidades vendidas, facturación y FECHA DE ÚLTIMA VENTA por item.id.
  // La última venta nos sirve como proxy del momento de quiebre cuando
  // el stock actual está en 0 (no tenemos timestamp explícito de "quedé sin stock").
  const agg = new Map()
  for (const v of ventas) {
    const fechaMs = (() => {
      const dc = v.date_created
      if (!dc) return null
      if (typeof dc === 'string') return new Date(dc).getTime()
      if (dc?.toDate) return dc.toDate().getTime()
      if (dc instanceof Date) return dc.getTime()
      return null
    })()
    for (const oi of (v.order_items || [])) {
      const itemId = oi?.item?.id
      if (!itemId) continue
      const qty = Number(oi.quantity || 0)
      const revenue = qty * Number(oi.unit_price || 0)
      const curr = agg.get(itemId) || { unidades: 0, facturacion: 0, ordenes: 0, ultimaVentaMs: 0 }
      curr.unidades    += qty
      curr.facturacion += revenue
      curr.ordenes     += 1
      if (fechaMs != null && fechaMs > curr.ultimaVentaMs) curr.ultimaVentaMs = fechaMs
      agg.set(itemId, curr)
    }
  }

  // Cruzar con items y calcular métricas
  const filas = []
  for (const it of items) {
    const itemId = (it.itemId ?? it.id)
    const a = agg.get(itemId) || { unidades: 0, facturacion: 0, ordenes: 0, ultimaVentaMs: 0 }
    // Regla: solo publicaciones CON ventas en la ventana
    if (a.unidades <= 0) continue

    const stock = Number((it.availableQuantity ?? it.available_quantity) || 0)
    const precio = Number(it.price || 0)
    const velocidadDiaria = a.unidades / ventanaDias
    const diasHastaQuiebre = velocidadDiaria > 0 ? (stock / velocidadDiaria) : Infinity
    // Tiempo de reposición del producto vinculado + productoId + costoUnit.
    // Enriquecemos cada row con todo lo derivado del catálogo para que los
    // memos posteriores (rotacion, columnas, links a producto) no tengan
    // que volver a resolver el map.
    const reposInfo = tiempoReposicionPorItem.get(itemId) || null
    const tiempoReposicion = reposInfo?.dias || null
    const productoId = reposInfo?.productoId || null
    const costoUnit = reposInfo?.costoUnit || null
    // Cobertura = días de stock − días que tarda en reponerse.
    // > 0 → llega la reposición antes del quiebre (con holgura de N días)
    // < 0 → vas a estar en quiebre durante N días esperando que llegue
    const cobertura = (tiempoReposicion != null && isFinite(diasHastaQuiebre))
      ? diasHastaQuiebre - tiempoReposicion
      : null
    const usaModoReposicion = (modo === 'reposicion' || modo === 'mixto') && tiempoReposicion != null
    // Umbrales aplicados a esta fila — los guardamos para usar el mismo
    // criterio en el render de la columna "Días a quiebre" (color del valor)
    // y mantener consistencia visual con la card de urgencia.
    const umbrales = calcularUmbrales({ modo, tiempoReposicion, diasCubrir })
    const urgencia = clasificarUrgencia(stock, diasHastaQuiebre, {
      modo,
      tiempoReposicion,
      diasCubrir,
    })
    // Fecha estimada de quiebre
    const fechaQuiebre = ahoraMs != null && stock > 0 && isFinite(diasHastaQuiebre)
      ? new Date(ahoraMs + diasHastaQuiebre * 86400000)
      : null

    // ─── Plata perdida / en riesgo ──────────────────────────────────────
    // Plata perdida (Quebrado, stock === 0):
    //   Usamos la última venta dentro de la ventana como proxy del momento
    //   en que se quebró. Asumimos que ese día todavía vendió, así que
    //   el lucro cesante arranca al día siguiente.
    //     diasSinVender = (ahora − ultimaVenta) / 86400000
    //     plataPerdida  = velocidadDiaria × max(0, diasSinVender − 1) × precio
    //
    // Plata en riesgo (Crítico, cobertura < 0):
    //   Solo en modo reposición, cuando vas a estar quebrado N días
    //   esperando que llegue el pedido. La pérdida proyectada es:
    //     plataEnRiesgo = velocidadDiaria × |cobertura| × precio
    let diasSinVender = null
    let plataPerdida = 0
    if (ahoraMs != null && stock <= 0 && a.ultimaVentaMs > 0) {
      diasSinVender = (ahoraMs - a.ultimaVentaMs) / 86400000
      if (diasSinVender > 1 && velocidadDiaria > 0 && precio > 0) {
        plataPerdida = velocidadDiaria * (diasSinVender - 1) * precio
      }
    }
    let plataEnRiesgo = 0
    if (cobertura != null && cobertura < 0 && velocidadDiaria > 0 && precio > 0) {
      plataEnRiesgo = velocidadDiaria * Math.abs(cobertura) * precio
    }

    // ─── Cuánto pedir según el estado actual ─────────────────────────
    // Modelo unificado: N = umbrales.N (R en reposición, dc en genérico).
    // Se calculan dos valores:
    //   pedirMin → llegar a 1N días al recibir (borde Crítico/Bajo, lo justo
    //              para no quebrar al próximo ciclo)
    //   pedirOk  → llegar a 2N días al recibir (operación normal, OK)
    //
    // La función calcularPedido() aplica la fórmula piecewise que evita el
    // sobreestimado clásico cuando stock < V × N (ítem que va a quebrar
    // antes de recibir el pedido).
    const N_pedido = umbrales.N
    const pedirMin = calcularPedido({
      velocidadDiaria,
      stock,
      leadTimeDias: N_pedido,
      objetivoDiasAlRecibir: N_pedido,           // 1N al recibir
    })
    const pedirParaOk = calcularPedido({
      velocidadDiaria,
      stock,
      leadTimeDias: N_pedido,
      objetivoDiasAlRecibir: N_pedido * MULT_OK, // 2N al recibir
    })
    const objetivoMinDias = N_pedido
    const objetivoOkDias  = N_pedido * MULT_OK

    filas.push({
      ...it,
      _unidadesVentana: a.unidades,
      _facturacionVentana: a.facturacion,
      _ordenesVentana: a.ordenes,
      _ultimaVentaMs: a.ultimaVentaMs || 0,
      _stock: stock,
      _velocidadDiaria: velocidadDiaria,
      _diasHastaQuiebre: diasHastaQuiebre,
      _fechaQuiebre: fechaQuiebre,
      _urgencia: urgencia,
      _tiempoReposicion: tiempoReposicion,
      _cobertura: cobertura,
      _usaModoReposicion: usaModoReposicion,
      _pedirMin: pedirMin,
      _pedirParaOk: pedirParaOk,
      _objetivoMinDias: objetivoMinDias,
      _objetivoOkDias: objetivoOkDias,
      _diasSinVender: diasSinVender,
      _plataPerdida: plataPerdida,
      _plataEnRiesgo: plataEnRiesgo,
      _uCrit: umbrales.uCrit,
      _uBajo: umbrales.uBajo,
      _uOk:   umbrales.uOk,
      // F8.6: enrich con productoId + costoUnit para que columnas y memos
      // posteriores (rotacion, link a producto)
      // no tengan que volver a resolver el map.
      _productoId: productoId,
      _costoUnit: costoUnit,
    })
  }

  // Por urgencia asc (quebrado primero) y luego por días asc. Las secciones lo
  // heredan: dentro de las sobradas entra primero la MENOS sobrada.
  filas.sort((a, b) => {
    const ra = URGENCIA[a._urgencia]?.rank ?? 99
    const rb = URGENCIA[b._urgencia]?.rank ?? 99
    if (ra !== rb) return ra - rb
    return (a._diasHastaQuiebre || 0) - (b._diasHastaQuiebre || 0)
  })

  return filas
}

// Corte de visitas/30d que separa las sobradas con tráfico (→ promo) de las
// sin tráfico (→ publicidad). Una sola copia: la vista lo importa.
export const UMBRAL_VISITAS_PROMO_VS_AD = 1000

// Días que una sobrada «descansa» de una acción (promo o publicidad) que su
// producto ya tuvo: ventana del cooldown de bitácora. Una sola copia.
export const COOLDOWN_DIAS = 30

// Umbrales canónicos. El agente puede pasar `umbrales` para overridear los
// defaults. Estos defaults son los mismos que usa el dashboard. La ventana de
// ventas NO está acá: no es un umbral, es cómo se cargaron los datos (report
// djynjlXICuUF0Yl803M8) y llega en `ventanaVentasDias`.
const DEFAULTS = {
  diasCubrir: 30,
  umbralVisitasPromoVsAd: UMBRAL_VISITAS_PROMO_VS_AD,
}

// Tiene promo activa: dealIds[] no vacío OR originalPrice > price.
function tienePromoActiva(p) {
  const deals = Array.isArray(p.dealIds) ? p.dealIds : []
  if (deals.length > 0) return true
  const op = Number(p.originalPrice)
  const px = Number(p.price)
  return Number.isFinite(op) && Number.isFinite(px) && op > px
}

// Margen sobre precio y espacio sobre el neto promedio de la cuenta: la única
// copia de la fórmula. La usan la sugerencia de la vista y la fila del agente.
//   margen     = (precio − costoUnit) / precio      (null sin costo o sin precio)
//   espacioPct = margen − netoTarget                (null sin neto de la cuenta)
function margenYEspacio(precio, costoUnit, netoTarget) {
  if (costoUnit == null || costoUnit <= 0 || !(precio > 0)) return { margen: null, espacioPct: null }
  const margen = (precio - costoUnit) / precio
  return { margen, espacioPct: netoTarget == null ? null : margen - netoTarget }
}

// Espacio para promo / ACOS, en la forma que muestra la vista.
//   { tipo: 'sin_costo' }                       — falta costo o precio
//   { tipo: 'sin_target' }                      — la empresa no tiene aprendizajes calculados
//   { tipo: 'sin_espacio', margen, target }     — el margen ya está debajo del benchmark
//   { tipo: 'ok', espacioPct, margen, target }  — espacio disponible (en pct sobre precio)
function sugerenciaDeEspacio(fila, costoUnit, netoTarget) {
  const { margen, espacioPct } = margenYEspacio(Number(fila.price || 0), costoUnit, netoTarget)
  if (margen == null) return { tipo: 'sin_costo' }
  if (netoTarget == null) return { tipo: 'sin_target' }
  if (espacioPct <= 0) return { tipo: 'sin_espacio', margen, target: netoTarget }
  return { tipo: 'ok', espacioPct, margen, target: netoTarget }
}

// ─── Secciones accionables sobre las filas de velocidad ─────────────────────
// Extraída tal cual del memo `rotacion` de VelocidadConsumoDashboard.jsx
// (report gqpLaPCkeyOj9uvmF5xg, PR 2a). Devuelve las cuatro secciones con las
// claves del agente —el contrato que lee el motor— y el desglose de las sobradas.
// El desglose no repite `bitacoraActiva` ni `cooldownDias`, que el memo sí
// devolvía: son entradas, no resultados, y la vista los lee de su propio estado.
// `gateBloqueados` lista, en orden, las sobradas que el gate de promo bloqueó:
// el motor las usa para la cancelación retroactiva (la vista no lo lee).
//   reponer_quiebre        → urgencia quebrado
//   reponer_velocidad      → urgencia crítica o baja
//   activar_promo_sobrado  → sobrada, visitas ≥ umbral, sin promo, fuera de
//                            cooldown y con el gate de promo sin bloquear
//   activar_ad_sobrado     → sobrada, visitas < umbral, sin ad, fuera de
//                            cooldown y sin veto ni sin_espacio del gate de ads
//
// Entrada:
//   filas               : salida de `filasVelocidadConsumo`, cuyas filas traen además
//                         del item `price`, `dealIds`, `originalPrice`, `_categoryId`
//                         y `_rentabilidadInferida` (la vista los arma en `items`)
//   productoInfoPorItem : Map itemId → { productoId, costoUnit }
//   visitas30dPorItem   : Map itemId → visitas de los últimos 30 días
//   itemIdsConAdActivo  : Set de itemIds con ad vivo
//   umbralVisitas       : corte promo vs ad (default `UMBRAL_VISITAS_PROMO_VS_AD`)
//   netoTarget          : neto promedio de la cuenta, o null
//   cooldown            : null (sin bitácora) o { ad, promo, claveAds, claveCanal }:
//                         `ad`/`promo` son Map productoId → Set de plataformas con
//                         evento en la ventana; `claveAds`/`claveCanal` dicen qué
//                         plataforma descarta. Maps vacíos equivalen a null.
//   infoRentPorItem, metricsAdsMap, estructuraCuenta, ecosistema,
//   impuestosEmpresa, benchmarksEmpresa : insumos de los gates de rentabilidad
export function seccionesVelocidadConsumo({
  filas = [],
  productoInfoPorItem = new Map(),
  visitas30dPorItem = new Map(),
  itemIdsConAdActivo = new Set(),
  umbralVisitas = UMBRAL_VISITAS_PROMO_VS_AD,
  netoTarget = null,
  cooldown = null,
  infoRentPorItem = new Map(),
  metricsAdsMap = new Map(),
  estructuraCuenta = null,
  ecosistema = null,
  impuestosEmpresa = null,
  benchmarksEmpresa = null,
}) {
  const reponer_quiebre = []
  const reponer_velocidad = []
  const activar_promo_sobrado = []
  const activar_ad_sobrado = []
  // Desglose de las sobradas, para que la UI muestre por qué cada una quedó o no.
  let total = 0
  let altoTrafico = 0
  let altoConPromo = 0
  let bajoTrafico = 0
  let bajoConAd = 0          // ya tiene ad activo → descartada (split limpio 12-jul)
  let bajoSinAd = 0          // entra a ad
  let sinVisitas = 0
  let cooldownDescartadasAd = 0
  let cooldownDescartadasPromo = 0
  let sinEspacioRent = 0     // gate de rentabilidad (retrofit 12-jul)
  let adsGastanSinVender = 0 // gastó en ads y no vendió → pausar_ad, jamás promo
  let adAPerdida = 0         // margen − ACOS esperado < 0 → veto
  let adSinEspacio = 0       // proyectado bajo la mínima → declarada
  const bloqueadosPromo = []

  for (const r of filas) {
    const itemId = String(r.itemId ?? r.id)
    const info = productoInfoPorItem.get(itemId) || null
    const costoUnit = info?.costoUnit || null
    const productoId = info?.productoId || null
    const visitas30d = visitas30dPorItem.get(itemId) || 0
    const fila = { ...r, _visitas30d: visitas30d, _costoUnit: costoUnit, _productoId: productoId }

    if (r._urgencia === 'quebrado') {
      reponer_quiebre.push(fila)
    } else if (r._urgencia === 'critico' || r._urgencia === 'bajo') {
      reponer_velocidad.push(fila)
    } else if (r._urgencia === 'sobrado') {
      total++
      if (visitas30d === 0) sinVisitas++
      // Cooldown: el producto ya tuvo evento de ese tipo, en la plataforma que
      // este tablero sugiere, dentro de la ventana de la bitácora.
      const enCooldownAd = !!(cooldown && productoId && cooldown.ad?.get(productoId)?.has(cooldown.claveAds))
      const enCooldownPromo = !!(cooldown && productoId && cooldown.promo?.get(productoId)?.has(cooldown.claveCanal))

      if (visitas30d >= umbralVisitas) {
        // Con tráfico: lo que falta es incentivo.
        altoTrafico++
        if (tienePromoActiva(r)) {
          altoConPromo++
        } else if (enCooldownPromo) {
          cooldownDescartadasPromo++
        } else {
          // Gate inferido: fórmula completa + gasto real de ads de la pub (TACOS).
          const gate = gatePromoConCostosReales({
            precio: Number(r.price) || 0,
            rent: infoRentPorItem.get(itemId) || { costoUnit: null, corte: null },
            metricsAds: metricsAdsMap.get(itemId) || null,
            ventasItem: { unidades: r._unidadesVentana, facturacion: r._facturacionVentana },
            categoryId: r._categoryId ?? null,
            estructuraCuenta, ecosistema, impuestosEmpresa,
          })
          if (gate.estado === 'sin_espacio') {
            if (gate.motivo === 'ads_gastan_sin_vender') adsGastanSinVender++
            else sinEspacioRent++
            bloqueadosPromo.push(itemId)
          } else {
            activar_promo_sobrado.push({ ...fila, _sugerencia: sugerenciaDeEspacio(r, costoUnit, netoTarget), _motivoPromo: 'alto_trafico', _gate: gate, metricas: { gate: { estado: gate.estado, margenActualPct: gate.margenActualPct, cortePct: gate.cortePct, descuentoMaxPct: gate.descuentoMaxPct, precioPiso: gate.precioPiso } } })
          }
        }
      } else {
        // Sin tráfico: le falta visibilidad. Con ad activo queda descartada.
        bajoTrafico++
        if (itemIdsConAdActivo.has(itemId)) {
          bajoConAd++
        } else {
          bajoSinAd++
          if (enCooldownAd) {
            cooldownDescartadasAd++
          } else {
            // Gate de ads: margen proyectado con ACOS esperado (cuenta → eco).
            const rentAd = infoRentPorItem.get(itemId) || { costoUnit: null, corte: null }
            const gateAd = gateAdsPrender({
              precio: Number(r.price) || 0,
              margenReal: Number.isFinite(r._rentabilidadInferida?.margenPctProm) ? r._rentabilidadInferida.margenPctProm : null,
              costoUnit: rentAd.costoUnit, corte: rentAd.corte,
              categoryId: r._categoryId ?? null,
              estructuraCuenta, ecosistema, impuestosEmpresa, benchmarksEmpresa,
            })
            if (gateAd.estado === 'veto_perdida') adAPerdida++
            else if (gateAd.estado === 'sin_espacio') adSinEspacio++
            else activar_ad_sobrado.push({ ...fila, _sugerencia: sugerenciaDeEspacio(r, costoUnit, netoTarget), _gateAds: gateAd, _motivoAd: textoGateAds(gateAd) })
          }
        }
      }
    }
  }

  return {
    secciones: { reponer_quiebre, reponer_velocidad, activar_promo_sobrado, activar_ad_sobrado },
    sobradoBreakdown: {
      total, altoTrafico, altoConPromo, bajoTrafico, bajoSinAd, bajoConAd, sinVisitas,
      accionablesPromo: activar_promo_sobrado.length,
      accionablesAd: activar_ad_sobrado.length,
      cooldownDescartadasAd, cooldownDescartadasPromo, sinEspacioRent,
      adsGastanSinVender, adAPerdida, adSinEspacio,
    },
    gateBloqueados: { activar_promo_sobrado: bloqueadosPromo },
  }
}

// Categoría de la publicación para los gates: una sola regla, la usan la vista
// (al armar sus ítems) y el agente.
export function categoryIdDePublicacion(publicacion) {
  return publicacion?.datosCanal?.categoryId ?? null
}

// Fila de las puras → forma que el agente entrega al motor. Cerrada, sin
// `...fila`: `tipo`/`hallazgos` de la publicación los lee el motor del ítem
// (agentes.js `recursoGenericoDe`) y no tienen que llegar a la tarea.
function filaAgente(fila, netoTarget, seccion) {
  const precio = Number(fila.price || 0)
  const { margen, espacioPct } = margenYEspacio(precio, fila._costoUnit, netoTarget)
  return {
    id: fila.itemId,
    _itemId: fila.itemId,
    _titulo: fila.title || fila.itemId,
    _permalink: fila.permalink || null,
    _productoId: fila._productoId,
    _stock: fila._stock,
    _velocidadDiaria: fila._velocidadDiaria,
    _diasHastaQuiebre: fila._diasHastaQuiebre,
    _urgencia: fila._urgencia,
    _tiempoReposicion: fila._tiempoReposicion,
    _visitas30d: fila._visitas30d,
    _uCrit: fila._uCrit,
    _uBajo: fila._uBajo,
    _uOk: fila._uOk,
    _precio: precio,
    _costoUnit: fila._costoUnit,
    _margenPct: margen,
    _netoTargetPct: netoTarget,
    _espacioPct: espacioPct,
    _precioMinimoSugerido: espacioPct > 0 ? precio * (1 - espacioPct) : null,
    _pedirMin: fila._pedirMin,
    _pedirOk: fila._pedirParaOk,
    _objetivoMinDias: fila._objetivoMinDias,
    _objetivoOkDias: fila._objetivoOkDias,
    ...(seccion === 'activar_promo_sobrado' ? { _motivoPromo: fila._motivoPromo, _gate: fila._gate, metricas: fila.metricas } : {}),
    ...(seccion === 'activar_ad_sobrado' ? { _gateAds: fila._gateAds, _motivoAd: fila._motivoAd } : {}),
  }
}

export function clasificarVelocidadConsumo({
  publicaciones = [],
  ventas30d = [],
  adsItems = [],
  productos = [],
  catalogoLinks = [],
  mlPublicacionesVisitas = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,
  config = {},          // doc empresas/{id} — leemos _aprendizajes.netoPctProm
  umbrales = {},
  impuestosEmpresa = null,   // loader _impuestos (config fiscal mergeada)
  ecosistema = null,         // doc conocimientoEcosistema/costos-ml (loader)
  // La ventana con la que el loader cargó `ventas30d` (`datos.ventanaVentasDias`,
  // agentes.js). Es el DIVISOR de la velocidad, así que tiene que ser la misma
  // que el numerador: hasta el report djynjlXICuUF0Yl803M8 el divisor salía del
  // `ventanaDias` de los umbrales del agente (con su default de 30) y los dos
  // números coincidían sólo porque el registry declara 30. `umbrales` ya no se
  // lee para esto, ni de fallback: el destructuring es CERRADO y el nombre
  // coincide con el campo que viaja (molde: contribucionNeta.js).
  ventanaVentasDias = null,
  // Eventos de la bitácora y el corte de su ventana (`datos.desdeCooldownMs`,
  // lo pone el loader). Sin corte no hay cooldown.
  productoBitacora = [],
  desdeCooldownMs = null,
}) {
  // Número o `null`, nunca otra cosa (ver `ventanaDeVentas`).
  const ventanaDias = ventanaDeVentas(ventanaVentasDias)
  const diasCubrir = umbrales.diasCubrir || DEFAULTS.diasCubrir
  const UMBRAL_VISITAS = umbrales.umbralVisitasPromoVsAd || DEFAULTS.umbralVisitasPromoVsAd

  // ─── netoTarget: neto promedio histórico de la cuenta ─────────────────
  // Mismo source que el dashboard (su `netoTarget`, VelocidadConsumoDashboard.jsx):
  // empresas/{id}._aprendizajes.netoPctProm. Si no está calculado, queda null
  // y los items entran sin recomendación de ACOS/descuento (el operador igual
  // ve precio + costo + margen en la tarea).
  const aprend = (config && config._aprendizajes) || null
  const netoTarget = (aprend?.estado === 'calculado' && typeof aprend?.netoPctProm === 'number')
    ? aprend.netoPctProm
    : null

  // ── Gate de rentabilidad (retrofit 12-jul — mismo criterio que Full) ──
  // Corte por cascada ADR 032 (producto→categoría→marca→proveedor→empresa).
  // Aplica SOLO a activar_promo_sobrado: sin espacio NO se sugiere promo
  // (declarado en analisis.sobradoSinEspacioRent); sin datos se sugiere con ⚠.
  const infoRentPorItem = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  // Gate INFERIDO (15-jul): fórmula completa + gasto real de ads de la pub.
  const estructuraCuenta = estructuraCostosML(ventas30d)
  const metricsAdsMap = metricasAdsPorItem(adsItems)

  // ─── 1. Mapa productoInfoPorItem (catalogoLinks × productos) ───────────
  const prodById = new Map()
  for (const p of productos) prodById.set(p.id, p)
  const productoInfoPorItem = new Map()
  for (const link of catalogoLinks) {
    if (link.plataformaId && link.plataformaId !== 'mercado-libre') continue
    const prod = prodById.get(link.productoId)
    if (!prod) continue
    const diasRaw = Number(prod.tiempoReposicionDias)
    const costoRaw = Number(prod.costoPromedio)
    productoInfoPorItem.set(link.externalItemId, {
      dias: Number.isFinite(diasRaw) && diasRaw > 0 ? diasRaw : null,
      productoId: prod.id,
      costoUnit: Number.isFinite(costoRaw) && costoRaw > 0 ? costoRaw : null,
    })
  }

  // ─── 2. Modo efectivo: 'mixto' si al menos 1 producto tiene tiempo ───────
  let modoEfectivo = 'generico'
  for (const info of productoInfoPorItem.values()) {
    if (info?.dias != null && info.dias > 0) { modoEfectivo = 'mixto'; break }
  }

  // ─── 3. Set de ads INSCRIPTOS (C6g: esAdVivo, NO status==='active') ──────
  // El status del ad es intradía-volátil (hold/idle = vivo); filtrar por
  // 'active' contra la canónica da 0 — misma cura ML-validada que el
  // dashboard Velocidad (C6b). Agente = dashboard (regla de consistencia).
  const itemIdsConAdActivo = new Set()
  for (const ad of adsItems) {
    if (!esAdVivo(ad.status)) continue
    const itemId = ad.item_id || (ad.id ? String(ad.id).split('_').pop() : null)
    if (itemId) itemIdsConAdActivo.add(String(itemId))
  }

  // ─── 4. Mapa visitas 30d ───────────────────────────────────────────────
  const visitas30dPorItem = new Map()
  for (const v of mlPublicacionesVisitas) {
    const itemId = v.itemId || v.id
    if (itemId) visitas30dPorItem.set(String(itemId), Number(v._visitas30d || 0))
  }

  // ─── 5. Ítems normalizados: id como string y categoría para los gates ───
  const items = publicaciones.map(it => ({
    ...it,
    itemId: String(it.itemId ?? it.id),
    _categoryId: categoryIdDePublicacion(it),
  }))

  // SIN VENTANA (decisión de Mariano, 11-oct-2026, registrada en el report
  // djynjlXICuUF0Yl803M8, pregunta 29: «¿Un detector que no sabe cuántos días
  // de ventas está mirando deja de calcular y avisa?» → Sí). Se corta por la
  // compuerta que usa el insumo, no el clasificador entero:
  //   · stock 0 es «quebrado» sin mirar días (urgenciaStock.js), así que
  //     reponer_quiebre sigue saliendo, con las tasas y el pedido en `null`
  //     — un 0 se leería como respuesta («pedí 0 unidades»).
  //   · con stock NO se clasifica: cualquier días-hasta-quiebre inventado es
  //     peor que nada. Infinity da «sobrado» para todo el catálogo ⇒ promos y
  //     ads masivos, o sea gastar plata; un número da otra urgencia inventada.
  //   · reponer_quiebre sale en el ORDEN DE ENTRADA: sin días a quiebre no hay
  //     de dónde sacar prioridad, y armar una sería inventarla. Es la única
  //     sección de este clasificador que el motor recorta sin prioridad.
  // Otro contrato (todo en null): no pasa por las puras, que dividen por la
  // ventana. `lecturas` va SÓLO acá: declarar la ventana siempre inflaría el
  // «N de M fuentes» del motor con una fuente que nadie leyó.
  if (ventanaDias === null) {
    // Unidades vendidas por itemId: sólo las publicaciones con rotación cuentan.
    const unidades = new Map()
    for (const v of ventas30d) {
      for (const oi of (v.order_items || [])) {
        const id = oi?.item?.id
        if (id) unidades.set(id, (unidades.get(id) || 0) + Number(oi.quantity || 0))
      }
    }
    const reponer_quiebre = []
    let total = 0
    let sinVentana = 0
    for (const it of items) {
      if ((unidades.get(it.itemId) ?? 0) <= 0) continue
      total++
      const stock = Number((it.availableQuantity ?? it.available_quantity) || 0)
      if (stock > 0) { sinVentana++; continue }
      const info = productoInfoPorItem.get(it.itemId) || null
      reponer_quiebre.push({
        id: it.itemId,
        _itemId: it.itemId,
        _titulo: it.title || it.itemId,
        _permalink: it.permalink || null,
        _productoId: info?.productoId || null,
        _stock: stock,
        _velocidadDiaria: null,
        _diasHastaQuiebre: null,
        _urgencia: 'quebrado',
        _tiempoReposicion: info?.dias || null,
        _visitas30d: visitas30dPorItem.get(it.itemId) || 0,
        _precio: Number(it.price || 0),
        _costoUnit: info?.costoUnit || null,
        _pedirMin: null,
        _pedirOk: null,
      })
    }
    return {
      secciones: { reponer_quiebre, reponer_velocidad: [], activar_promo_sobrado: [], activar_ad_sobrado: [] },
      analisis: {
        total,
        porUrgencia: { quebrado: reponer_quiebre.length, critico: 0, bajo: 0, ok: 0, sobrado: 0 },
        sinVentana,
        sumStock: 0,
        sumValuacion: 0,
        sumPotencial: 0,
        sobradoSinEspacioRent: 0,
        sobradoAdAPerdida: 0,
        sobradoAdsGastanSinVender: 0,
        sobradoAdSinEspacio: 0,
        sobradoCooldownPromo: 0,
        sobradoCooldownAd: 0,
        modoEfectivo,
        ventanaDias,
        diasCubrir,
      },
      gateBloqueados: { activar_promo_sobrado: [] },
      lecturas: {
        fuentes: [FUENTE_VENTANA],
        degradaciones: [{ fuente: FUENTE_VENTANA, motivo: 'sin-dato: no se sabe con cuántos días de ventas se cargaron los datos — no se calcularon velocidad, días hasta quiebre ni pedido' }],
      },
    }
  }

  // ─── 6. Filas y secciones: las mismas puras que la vista ───────────────
  // Con el orden de la vista (el motor recorta a los primeros N; report
  // scoEqWsgJX991pQovyvL), y sin reloj: el corte del cooldown lo pone el loader.
  const filas = filasVelocidadConsumo({
    items,
    ventas: ventas30d,
    ventanaDias,
    tiempoReposicionPorItem: productoInfoPorItem,
    modo: modoEfectivo,
    diasCubrir,
  })
  const { secciones, sobradoBreakdown, gateBloqueados } = seccionesVelocidadConsumo({
    filas,
    productoInfoPorItem,
    visitas30dPorItem,
    itemIdsConAdActivo,
    umbralVisitas: UMBRAL_VISITAS,
    netoTarget,
    cooldown: desdeCooldownMs == null ? null : {
      ...cooldownDesdeBitacora(productoBitacora, { desdeMs: desdeCooldownMs }),
      claveAds: PLATAFORMA_ADS_ML,
      claveCanal: CANAL_ML,
    },
    infoRentPorItem,
    metricsAdsMap,
    estructuraCuenta,
    ecosistema,
    impuestosEmpresa,
    benchmarksEmpresa: config?._benchmarksML || null,
  })

  const porUrgencia = { quebrado: 0, critico: 0, bajo: 0, ok: 0, sobrado: 0 }
  let sumStock = 0
  let sumValuacion = 0
  let sumPotencial = 0
  for (const f of filas) {
    porUrgencia[f._urgencia] = (porUrgencia[f._urgencia] || 0) + 1
    sumStock += f._stock
    if (f._costoUnit > 0) sumValuacion += f._stock * f._costoUnit
    const precio = Number(f.price || 0)
    if (precio > 0) sumPotencial += f._stock * precio
  }

  return {
    secciones: Object.fromEntries(Object.entries(secciones).map(([k, fs]) => [k, fs.map(f => filaAgente(f, netoTarget, k))])),
    analisis: {
      total: filas.length,
      porUrgencia,
      sinVentana: 0,
      sumStock,
      sumValuacion,
      sumPotencial,
      sobradoSinEspacioRent: sobradoBreakdown.sinEspacioRent,
      sobradoAdAPerdida: sobradoBreakdown.adAPerdida,                  // flywheel: a pérdida no se publicita
      sobradoAdsGastanSinVender: sobradoBreakdown.adsGastanSinVender,  // pausar_ad, no promo
      sobradoAdSinEspacio: sobradoBreakdown.adSinEspacio,              // proyectado abajo de la mínima
      // En cooldown: descansan, no se bloquean (no van a `gateBloqueados`).
      sobradoCooldownPromo: sobradoBreakdown.cooldownDescartadasPromo,
      sobradoCooldownAd: sobradoBreakdown.cooldownDescartadasAd,
      modoEfectivo,
      ventanaDias,
      diasCubrir,
    },
    // Ítems bloqueados por el gate inferido → cancelación retroactiva (motor).
    gateBloqueados,
  }
}
